// src/App.js
import React, { useEffect, useState, useRef } from "react";
import "./App.css";
import TermsGate from "./TermsGate";
import { useAuth0 } from "@auth0/auth0-react";
import { hasPageAccess, getAllowedClients } from "./config/userAccess";
import { LoadingScreen, LoginGateway, AccessDeniedScreen } from "./AuthScreens";
import "mapbox-gl/dist/mapbox-gl.css";
import { Routes, Route, useParams, Navigate } from "react-router-dom";

import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import {
  getClientConfig,
  getStateBundle,
  getScores,
  getOverlayMeta,   
  getOverlays,      
} from "./api/twynpopApi";
import { stateNameByFips } from "./config/statefipsDictionary";
import {
  canAddScenario,
  canSelectTargetVariable,
} from "./config/clientLimits";


import {
  demographicVarLabels,
  targetVarLabels,
  targetPackageLabels,
  levelsMap,
  disabilityChildVars,
  hinsClassVars,
  hinsSourceVars,
  atticVars,
  compositeConfig,
  overlayDefinitions, 
} from "./config/twynpopDictionary";

import twynLogo from "./assets/twyn-logo.png";
import userGuidePdf from "./assets/twyn-user-guide.pdf";
import { FiMap, FiInfo, FiDownload, FiLogOut } from "react-icons/fi";

import JSZip from "jszip";
import { saveAs } from "file-saver";

const ALL_CLIENTS = [
  "Demo",
  "Capsule",
  "BCBS",
  "Kidde",
  "Amrize",
  "Bush's",
  "Carrier",
  "U Rochester",
  "VELUX"
];


// Mapbox token from .env
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const MAP_STYLES = {
  dark: "mapbox://styles/mapbox/dark-v11",
  light: "mapbox://styles/mapbox/light-v11",
  satellite: "mapbox://styles/mapbox/satellite-streets-v12",
};

let nextScenarioId = 1;
const exportFontFamily =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";

// Mapbox exports its WebGL canvas, but not React overlays. Add the scenario
// summary card to a copy of that canvas so exported images match the map view.
function wrapCanvasText(context, text, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (line && context.measureText(candidate).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  });

  if (line) lines.push(line);
  return lines.length ? lines : [""];
}

function drawRoundedRect(context, x, y, width, height, radius) {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.arcTo(x + width, y, x + width, y + height, safeRadius);
  context.arcTo(x + width, y + height, x, y + height, safeRadius);
  context.arcTo(x, y + height, x, y, safeRadius);
  context.arcTo(x, y, x + width, y, safeRadius);
  context.closePath();
}

function exportMapWithScenarioSummary({
  mapCanvas,
  mapRect,
  panelRect,
  twynCount,
  scenarioNumber,
  scenarioDescription,
}) {
  if (!mapCanvas || !mapRect.width || !mapRect.height || !panelRect) {
    return mapCanvas?.toDataURL("image/png") || null;
  }

  const exportCanvas = document.createElement("canvas");
  exportCanvas.width = mapCanvas.width;
  exportCanvas.height = mapCanvas.height;

  const context = exportCanvas.getContext("2d");
  if (!context) return mapCanvas.toDataURL("image/png");

  context.drawImage(mapCanvas, 0, 0);

  // Canvas dimensions may be scaled for a high-DPI display while DOMRect
  // values are CSS pixels. Work in CSS pixels after drawing the map layer.
  const scaleX = exportCanvas.width / mapRect.width;
  const scaleY = exportCanvas.height / mapRect.height;
  context.save();
  context.scale(scaleX, scaleY);

  const x = panelRect.left - mapRect.left;
  const y = panelRect.top - mapRect.top;
  const width = panelRect.width;
  const paddingX = 16;
  const paddingY = 12;
  const contentWidth = Math.max(width - paddingX * 2, 1);
  const titleLineHeight = 28;
  const descriptionLineHeight = 20;

  context.font = `700 25.6px ${exportFontFamily}`;
  const title = `${Number(twynCount || 0).toLocaleString()} Twins`;
  context.font = `12px ${exportFontFamily}`;
  const descriptionLines = wrapCanvasText(
    context,
    `Scenario ${scenarioNumber}: ${scenarioDescription}`,
    contentWidth
  );
  const height =
    paddingY +
    titleLineHeight +
    5 +
    descriptionLines.length * descriptionLineHeight +
    paddingY;

  context.save();
  context.shadowColor = "rgba(0, 0, 0, 0.45)";
  context.shadowBlur = 30;
  context.shadowOffsetY = 10;
  drawRoundedRect(context, x, y, width, height, 8);
  context.fillStyle = "rgba(2, 6, 23, 0.94)";
  context.fill();
  context.restore();

  drawRoundedRect(context, x, y, width, height, 8);
  context.strokeStyle = "#1f2937";
  context.lineWidth = 1;
  context.stroke();

  context.textBaseline = "top";
  context.font = `700 25.6px ${exportFontFamily}`;
  context.fillStyle = "#22D4F0";
  context.fillText(title, x + paddingX, y + paddingY);

  context.font = `12px ${exportFontFamily}`;
  context.fillStyle = "#9ca3af";
  descriptionLines.forEach((line, index) => {
    context.fillText(
      line,
      x + paddingX,
      y + paddingY + titleLineHeight + 5 + index * descriptionLineHeight
    );
  });

  context.restore();
  return exportCanvas.toDataURL("image/png");
}


// ---------------------------------------------------------------------------
// Shared scroll-box style (Restored internal scrolling)
// ---------------------------------------------------------------------------
const scrollBoxStyle = {
  border: "1px solid #1f2937",
  borderRadius: "6px",
  padding: "0.6rem 0.75rem",
  maxHeight: "260px",       // <--- Restored limit
  overflowY: "auto",        // <--- Restored scrolling
  background: "#020617",
};

// ---------------------------------------------------------------------------
// Helper: create an empty scenario given a client config
// ---------------------------------------------------------------------------
function makeEmptyScenario(cfg) {
  const initDemoSel = {};
  const initDemoOpen = {};
  (cfg.allowed_demographics || []).forEach((v) => {
    initDemoSel[v] = [];
    initDemoOpen[v] = false; // collapsed by default
  });

  // target var open states (per variable)
  const initTargetOpen = {};
  (cfg.allowed_targets || []).forEach((v) => {
    initTargetOpen[v] = false; // collapsed by default
  });

  // package open states (per package)
  const initTargetPackageOpen = {};
  const pkgObj = cfg.allowed_target_packages || {};
  const pkgNames = Array.isArray(pkgObj) ? pkgObj : Object.keys(pkgObj);

  pkgNames.forEach((p) => {
    initTargetPackageOpen[p] = false; // collapsed by default
  });

  return {
    scenarioId: `scenario-${nextScenarioId++}`,
    demographicSelections: initDemoSel,
    demoOpen: initDemoOpen,
    selectedTargetVars: [],
    targetValues: {},
    selectedDisabilityChildren: [],
    selectedHinsClass: [],
    selectedHinsSources: [],
    selectedAtticChildren: [],
    scoreResponse: null,

    compositeSelections: {},

    targetOpen: initTargetOpen,
    targetPackageOpen: initTargetPackageOpen,

    overlayOpen: true,                 // box open/closed if you want
    selectedOverlays: {},              // { overlayKey: true/false }

  };
}

// Keep an archetype limited to filter selections so it can be safely applied
// to another scenario without carrying over scores, map state, or UI state.
function copyScenarioSelections(scenario) {
  return {
    demographicSelections: Object.fromEntries(
      Object.entries(scenario.demographicSelections || {}).map(([variable, values]) => [
        variable,
        [...(values || [])],
      ])
    ),
    selectedTargetVars: [...(scenario.selectedTargetVars || [])],
    targetValues: Object.fromEntries(
      Object.entries(scenario.targetValues || {}).map(([variable, values]) => [
        variable,
        Array.isArray(values) ? [...values] : values,
      ])
    ),
    selectedDisabilityChildren: [...(scenario.selectedDisabilityChildren || [])],
    selectedHinsClass: [...(scenario.selectedHinsClass || [])],
    selectedHinsSources: [...(scenario.selectedHinsSources || [])],
    selectedAtticChildren: [...(scenario.selectedAtticChildren || [])],
    compositeSelections: { ...(scenario.compositeSelections || {}) },
  };
}

// ---------------------------------------------------------------------------
// Collapsible demographic variable with multi-select checkboxes
// ---------------------------------------------------------------------------
function DemographicGroup({
  varName,
  labelMap,
  isOpen,
  onToggle,
  selectedValues,
  onChangeValue,
  onClearSelection,   // NEW
}) {
  const prettyLabel = labelMap[varName] || varName;
  const levelDefs = levelsMap[varName] || {};
  const hasSelections = (selectedValues || []).length > 0;

  return (
    <div style={{ marginBottom: "0.4rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.35rem",
        }}
      >
        <button
          type="button"
          onClick={onToggle}
          style={{
            border: "none",
            background: "none",
            padding: 0,
            margin: 0,
            fontSize: "0.9rem",
            fontWeight: 600,
            cursor: "pointer",
            color: "#e5e7eb",
          }}
        >
          {isOpen ? "▾ " : "▸ "}
          {prettyLabel}
        </button>

        {hasSelections && typeof onClearSelection === "function" && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation(); // don't toggle section open/closed
              onClearSelection();
            }}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              margin: 0,
              fontSize: "0.75rem",
              cursor: "pointer",
              color: "#9ca3af",
            }}
            title="Clear selections"
          >
            ✕
          </button>
        )}
      </div>

      {isOpen && (
        <div style={{ marginTop: "0.25rem" }}>
          {Object.entries(levelDefs).map(([code, label]) => (
            <label
              key={code}
              style={{
                display: "block",
                fontSize: "0.85rem",
                marginLeft: "1.4rem",
                marginBottom: "0.15rem",
              }}
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(code)}
                onChange={() => onChangeValue(varName, code)}
                style={{ marginRight: "0.35rem" }}
              />
              {label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------
// Client page: reads :clientId and shows map
// ---------------------------------------------
function ClientPage() {
  const { clientId } = useParams();
  const { logout } = useAuth0();
  const isDemoClient = clientId?.toLowerCase() === "demo";



  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  // Shared state across scenarios
  const [selectedState, setSelectedState] = useState("");

  // Scenario state
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(0);
  const [savedArchetype, setSavedArchetype] = useState(null);

  const [baseMapStyle, setBaseMapStyle] = useState("dark");
  const [isMapMenuOpen, setIsMapMenuOpen] = useState(false);
  const [isInfoMenuOpen, setIsInfoMenuOpen] = useState(false);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [isLogoutMenuOpen, setIsLogoutMenuOpen] = useState(false);


  const [activeInfoItem, setActiveInfoItem] = useState(null); // 'userGuide' | null
  const [activeDownloadItem, setActiveDownloadItem] = useState(null); // 'image' | 'data' | null

  const [isComputing, setIsComputing] = useState(false);
  const [computeError, setComputeError] = useState(null);
  const canAddAnotherScenario = canAddScenario(
    isDemoClient,
    scenarios.length
  );
  const isScenarioAddDisabled = isComputing || !canAddAnotherScenario;

  // "Active" scoreResponse for the map & Twyn count (comes from the active scenario)
  const [scoreResponse, setScoreResponse] = useState(null);

  // -------------------------------------------------------------------
  // Geographic overlays (must not affect tracts/scores/scenarios)
  // -------------------------------------------------------------------
  const [overlayMeta, setOverlayMeta] = useState(null);     // availability per state

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const tractGeoJSONRef = useRef(null);
  const popupRef = useRef(null);

  const [overlaySelections, setOverlaySelections] = useState({}); 
  const overlayCacheRef = useRef({});

  const [twynCount, setTwynCount] = useState(0);
  const twynCountRef = useRef(0);
  const [shouldAnimateTwyns, setShouldAnimateTwyns] = useState(false);

  const twynPanelRef = useRef(null);

  const [is3DView, setIs3DView] = useState(true);
  const [opacity, setOpacity] = useState(0.85);

  const is3DViewRef = useRef(true);
  const opacityRef = useRef(0.85);        
  const scoreResponseRef = useRef(null);

  const stateBoundsRef = useRef(null);
  const geocoderRef = useRef(null); 
  const geocoderContainerRef = useRef(null);   

  // Helper: fly back to the current state's full bounds
  const flyToStateBounds = () => {
    const mapInst = mapRef.current;
    const bounds = stateBoundsRef.current;

    if (!mapInst || !bounds) return;

    mapInst.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      {
        padding: 40,
        duration: 800,
        essential: true,
      }
    );
  };   

  // ---------------------------------------------------------------------------
  // Generic Overlay Manager (Handles Mapbox Layers for ALL types)
  // ---------------------------------------------------------------------------
  const manageOverlayLayer = async (def, selectedBrands) => {
    const map = mapRef.current;
    if (!map || !selectedState) return;

    const LAYER_ID = `overlay-${def.key}`;
    const SOURCE_ID = `overlay-${def.key}`;
    const isActive = selectedBrands && selectedBrands.length > 0;

    // 1. If turned off, remove layer/source
    if (!isActive) {
      if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      return;
    }

    // 2. Fetch & Add Source (if missing)
    if (!map.getSource(SOURCE_ID)) {
      // Check cache first
      let geojson = overlayCacheRef.current[def.key];

      if (!geojson) {
        // Fetch from API
        try {
          const overlaysResp = await getOverlays(clientId, selectedState);
          geojson = overlaysResp?.[def.key];
          if (geojson) {
            overlayCacheRef.current[def.key] = geojson; // Cache it
          }
        } catch (err) {
          console.error(`Error loading overlay ${def.key}:`, err);
        }
      }

      if (!geojson) return; // No data found

      map.addSource(SOURCE_ID, { type: "geojson", data: geojson });

      map.addLayer({
        id: LAYER_ID,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-radius": 5,
          "circle-color": def.color,
          "circle-opacity": 0.65,
          "circle-stroke-width": 1,
          "circle-stroke-color": def.stroke || "#000",
          "circle-stroke-opacity": 0.8,
        },
      });

      // Generic Hover Tooltip
      map.on("mousemove", LAYER_ID, (e) => {
        if (!e.features?.length) return;
        const p = e.features[0].properties || {};
        popupRef.current
          .setLngLat(e.lngLat)
          .setHTML(`
            <div style="font-size:12px;line-height:1.4;">
              <strong>${p.Name || def.label}</strong><br/>
              ${p.brand || ""}<br/>
              ${p.Street || ""}<br/>
              ${p.City || ""} ${p.State || ""} ${p.ZIP || ""}<br/>
              ${p.Phone || ""}<br/>
              ${p.Open_Hours || ""}
            </div>
          `)
          .addTo(map);
      });

      map.on("mouseleave", LAYER_ID, () => {
        popupRef.current?.remove();
        map.getCanvas().style.cursor = "";
      });
      
      map.on("mouseenter", LAYER_ID, () => {
        map.getCanvas().style.cursor = "pointer";
      });
    }

    // 3. Apply Filters
    if (map.getLayer(LAYER_ID)) {
      if (selectedBrands.includes("All brands") || selectedBrands.includes("ALL")) {
        map.setFilter(LAYER_ID, null);
      } else {
        map.setFilter(LAYER_ID, ["in", "brand", ...selectedBrands]);
      }
    }
  };

  useEffect(() => {
    is3DViewRef.current = is3DView;
  }, [is3DView]);

  useEffect(() => {
    scoreResponseRef.current = scoreResponse;
  }, [scoreResponse]);

  useEffect(() => {
    opacityRef.current = opacity;  
  }, [opacity]);

  const applyOpacityToMap = (value) => {
    const map = mapRef.current;
    if (!map || !map.getLayer("tracts-fill")) return;
    map.setPaintProperty("tracts-fill", "fill-extrusion-opacity", value);
  };

  const handleOpacityChange = (value) => {
    setOpacity(value);
    applyOpacityToMap(value);
  };

  // Convenience: active scenario + its fields
  const activeScenario =
    scenarios.length > 0 ? scenarios[selectedScenarioIndex] : null;
  const savedArchetypeSourceIndex = savedArchetype
    ? scenarios.findIndex(
        (scenario) => scenario.scenarioId === savedArchetype.sourceScenarioId
      )
    : -1;

  const demoOpen = activeScenario?.demoOpen || {};
  const demographicSelections = activeScenario?.demographicSelections || {};
  const selectedTargetVars = activeScenario?.selectedTargetVars || [];
  const selectedDisabilityChildren = activeScenario?.selectedDisabilityChildren || [];
  const selectedHinsClass = activeScenario?.selectedHinsClass || [];
  const selectedHinsSources = activeScenario?.selectedHinsSources || [];
  const selectedAtticChildren = activeScenario?.selectedAtticChildren || [];

  const targetOpen = activeScenario?.targetOpen || {};
  const targetPackageOpen = activeScenario?.targetPackageOpen || {};

  // -------------------------------------------------------
  // Load client config
  // -------------------------------------------------------
  useEffect(() => {
    async function fetchConfig() {
      try {
        setStatus("loading");
        setError(null);
        setIsComputing(false);
        setComputeError(null);

        const cfg = await getClientConfig(clientId);
        setConfig(cfg);
        setStatus("loaded");

        // default: no state selected (show full US)
        setSelectedState("");

        // initialize scenarios (single empty scenario to start)
        const firstScenario = makeEmptyScenario(cfg);
        setScenarios([firstScenario]);
        setSelectedScenarioIndex(0);
        setSavedArchetype(null);

        // reset map-related
        setScoreResponse(null);
        setTwynCount(0);
        twynCountRef.current = 0;
        setShouldAnimateTwyns(false);
      } catch (err) {
        console.error(err);
        setConfig(null);
        setError(err.message || "Unknown error");
        setStatus("error");
      }
    }

    if (clientId) {
      fetchConfig();
    }
  }, [clientId]);

  // -------------------------------------------
  // Initialize base map (satellite streets + tilt) + 2D/3D toggle button
  // -------------------------------------------
  useEffect(() => {
    if (mapRef.current) return;

    if (!mapboxgl.accessToken) {
      console.error("No Mapbox access token found. Check .env.");
      return;
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLES[baseMapStyle],
      center: [-98.35, 39.5],
      zoom: 3.5,
      pitch: 65,
      bearing: 0,
      antialias: true,
      preserveDrawingBuffer: true,
    });

    mapRef.current = map;

    // Default zoom / pitch control
    map.addControl(new mapboxgl.NavigationControl(), "top-right");

    // Add our 2D/3D toggle into that same control group
    map.on("load", () => {
      const container = map.getContainer();
      const ctrlGroup = container.querySelector(".mapboxgl-ctrl-group");
      if (!ctrlGroup) return;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "mapboxgl-ctrl-icon";
      btn.setAttribute("aria-label", "Toggle 2D/3D view");
      btn.style.fontSize = "15px";
      btn.style.display = "flex";
      btn.style.alignItems = "center";
      btn.style.justifyContent = "center";

      // helper to update the icon + tooltip based on current mode
      const updateToggleIcon = () => {
        if (is3DViewRef.current) {
          btn.textContent = "⬛";
          btn.title = "Switch to 2D view";
        } else {
          btn.textContent = "▢";
          btn.title = "Switch to 3D view";
        }
      };

      updateToggleIcon();

      btn.onclick = () => {
        const mapInst = mapRef.current;
        if (!mapInst || !mapInst.getLayer("tracts-fill")) return;

        const next = !is3DViewRef.current;
        setIs3DView(next);
        is3DViewRef.current = next;

        if (next) {
          // 3D view
          mapInst.easeTo({ pitch: 65, duration: 500 });

          if (scoreResponseRef.current) {
            applyScoresToMap(scoreResponseRef.current, { animate: false });
          }
        } else {
          // 2D view
          mapInst.easeTo({ pitch: 0, duration: 500 });

          if (scoreResponseRef.current) {
            apply2DStyle();
          } else if (mapInst.getLayer("tracts-fill")) {
            mapInst.setPaintProperty("tracts-fill", "fill-extrusion-height", 0);
            mapInst.setPaintProperty("tracts-fill", "fill-extrusion-base", 0);
          }
        }

        updateToggleIcon();
      };

      ctrlGroup.appendChild(btn);

      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        marker: false,
        placeholder: "Search location…",
        countries: "us",
        types: "place,locality,neighborhood,postcode,district",
        flyTo: {
          maxZoom: 12,
        },
      });

      geocoderRef.current = geocoder;
      map.addControl(geocoder, "top-right");

      // --- PATCH: wrap geocoder.clear so it ALWAYS flies back to state bounds ---
      const originalClear = geocoder.clear.bind(geocoder);
      geocoder.clear = () => {
        originalClear();
        flyToStateBounds();
      };

      // (Optional) still keep the event, but it's now just a backup
      geocoder.on("clear", () => {
        flyToStateBounds();
      });

      // grab the DOM element so we can style/disable it
      const geocoderEl = container.querySelector(".mapboxgl-ctrl-geocoder");
      if (geocoderEl) {
        geocoderContainerRef.current = geocoderEl;

        geocoderEl.style.position = "absolute";
        geocoderEl.style.right = "4.5rem";
        geocoderEl.style.left = "auto";
        geocoderEl.style.margin = "0";
        geocoderEl.style.zIndex = "20";

        // Hide completely until a state is selected
        geocoderEl.style.display = "none";

        // ALSO: watch the underlying input – when user deletes everything
        const input = geocoderEl.querySelector("input");
        if (input) {
          input.addEventListener("input", () => {
            if (input.value === "") {
              flyToStateBounds();
            }
          });
        }
      }      

    });

    popupRef.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });


    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // keep [] so we only create the map once

  // Re-apply style when baseMapStyle changes
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    const currentPitch = map.getPitch();
    const currentBearing = map.getBearing();

    map.setStyle(MAP_STYLES[baseMapStyle]);

    // When the new style loads, restore our view and re-apply layers
    map.once("styledata", () => {
      map.jumpTo({
        center: currentCenter,
        zoom: currentZoom,
        pitch: currentPitch,
        bearing: currentBearing,
      });

      if (tractGeoJSONRef.current) {
        if (!map.getSource("tracts")) {
          map.addSource("tracts", {
            type: "geojson",
            data: tractGeoJSONRef.current,
          });
        }
        if (!map.getLayer("tracts-fill")) {
          map.addLayer({
            id: "tracts-fill",
            type: "fill-extrusion",
            source: "tracts",
            paint: {
              "fill-extrusion-color": "#ffffff",
              "fill-extrusion-opacity": 0.15,
              "fill-extrusion-height": 0,
              "fill-extrusion-base": 0,
            },
          });
        }
      }

      if (scoreResponseRef.current && map.getLayer("tracts-fill")) {
        applyScoresToMap(scoreResponseRef.current, { animate: false });

        if (!is3DViewRef.current) {
          apply2DStyle();
        }
      }

      // RESTORE ALL ACTIVE OVERLAYS
      overlayDefinitions.forEach(def => {
        const activeBrands = overlaySelections[def.key];
        if (activeBrands && activeBrands.length > 0) {
          manageOverlayLayer(def, activeBrands);
        }
      });
    }); 
  }, [baseMapStyle]);

  // -------------------------------------------
  // Overlay availability (metadata only) — does NOT touch the map
  // -------------------------------------------
  useEffect(() => {
    if (!selectedState) {
      setOverlayMeta(null);
      return;
    }

    (async () => {
      try {
        const meta = await getOverlayMeta(clientId, selectedState);
        setOverlayMeta(meta || null);
      } catch (e) {
        console.error("Overlay meta error:", e);
        setOverlayMeta(null);
      }
    })();
  }, [clientId, selectedState]);
  
  // -------------------------------------------
  // When selectedState changes:
  //  1) fly to that state
  //  2) fetch its tract GeoJSON and draw polygons
  // -------------------------------------------
  useEffect(() => {
    let cancelled = false;

    // -------------------------------
    // Overlay MUST clear on state change
    // -------------------------------
    const mapForOverlayClear = mapRef.current;
    if (mapForOverlayClear) {
      // hardware (existing)
      if (mapForOverlayClear.getLayer("overlay-hardware-stores")) {
        mapForOverlayClear.removeLayer("overlay-hardware-stores");
      }
      if (mapForOverlayClear.getSource("overlay-hardware-stores")) {
        mapForOverlayClear.removeSource("overlay-hardware-stores");
      }

      // food (NEW)
      if (mapForOverlayClear.getLayer("overlay-food-stores")) {
        mapForOverlayClear.removeLayer("overlay-food-stores");
      }
      if (mapForOverlayClear.getSource("overlay-food-stores")) {
        mapForOverlayClear.removeSource("overlay-food-stores");
      }
    }

    async function updateStateView() {
      const map = mapRef.current;
      if (!map) return;

      // reset Twyn count on state change
      setTwynCount(0);
      twynCountRef.current = 0;

      // clear scenario scoreResponses when state changes
      setScenarios((prev) =>
        prev.map((sc) => ({
          ...sc,
          scoreResponse: null,
        }))
      );
      setScoreResponse(null);

      // If state is cleared, reset to national view and remove state layer
      if (!selectedState) {
        tractGeoJSONRef.current = null;
        stateBoundsRef.current = null;

        if (geocoderRef.current) {
          geocoderRef.current.clear();
          geocoderRef.current.setBbox(null);
        }

        if (geocoderContainerRef.current) {
          geocoderContainerRef.current.style.display = "none";
        }

        // Remove tracts layer/source if present (optional — you can keep them too)
        if (map.getLayer("tracts-fill")) map.removeLayer("tracts-fill");
        if (map.getSource("tracts")) map.removeSource("tracts");

        map.flyTo({
          center: [-98.35, 39.5],
          zoom: 3.5,
          essential: true,
        });

        return;
      }

      try {
        // ONE request: returns bbox + geojson
        const bundle = await getStateBundle(selectedState);
        if (cancelled) return;

        const geojson = bundle.geojson;
        const bbox = bundle.bbox; // [minLng, minLat, maxLng, maxLat] (or null)

        tractGeoJSONRef.current = geojson;

        // FAST PATH: do NOT remove/add source+layer; just setData if exists
        const existingSrc = map.getSource("tracts");
        if (!existingSrc) {
          map.addSource("tracts", {
            type: "geojson",
            data: geojson,
          });
        } else if (existingSrc.setData) {
          existingSrc.setData(geojson);
        }

        if (!map.getLayer("tracts-fill")) {
          map.addLayer({
            id: "tracts-fill",
            type: "fill-extrusion",
            source: "tracts",
            paint: {
              "fill-extrusion-color": "#ffffff",
              "fill-extrusion-opacity": 0.15,
              "fill-extrusion-height": 0,
              "fill-extrusion-base": 0,
            },
          });
        } else {
          // reset base styling on state change
          map.setPaintProperty("tracts-fill", "fill-extrusion-color", "#ffffff");
          map.setPaintProperty("tracts-fill", "fill-extrusion-opacity", 0.15);
          map.setPaintProperty("tracts-fill", "fill-extrusion-height", 0);
          map.setPaintProperty("tracts-fill", "fill-extrusion-base", 0);
        }

        // use bbox from server (no client-side coordinate traversal)
        if (bbox && bbox.length === 4) {
          stateBoundsRef.current = bbox;

          if (geocoderRef.current) {
            geocoderRef.current.clear();
            geocoderRef.current.setBbox(bbox);
          }

          if (geocoderContainerRef.current) {
            geocoderContainerRef.current.style.display = "block";
            const input = geocoderContainerRef.current.querySelector("input");
            if (input) input.placeholder = "Search location…";
          }

          map.fitBounds(
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            ],
            { padding: 40, duration: 800, essential: true }
          );
        }

        // ----- HOVER TOOLTIP -----
        const popup = popupRef.current;

        // IMPORTANT: create stable handler fns and detach them properly
        if (!map.__twynHoverHandlers) map.__twynHoverHandlers = {};

        // remove previous handlers if they exist
        const prevMove = map.__twynHoverHandlers.move;
        const prevLeave = map.__twynHoverHandlers.leave;
        if (prevMove) map.off("mousemove", "tracts-fill", prevMove);
        if (prevLeave) map.off("mouseleave", "tracts-fill", prevLeave);

        const onMove = (e) => {
          if (!e.features || !e.features.length || !popup) return;

          const f = e.features[0];
          const props = f.properties || {};

          const tractId = props.GEOID || props.census_tract || props.geoid || "";
          const nTotal = props.n_total ?? props.n ?? "";
          const nTarget = props.n_target ?? "";
          const pctTarget =
            props.pct_target != null ? `${Number(props.pct_target).toFixed(1)}%` : "";
          const scoreVal =
            props.priority_score_100 != null ? Number(props.priority_score_100).toFixed(0) : "";

          const html = `
            <div style="font-size:12px; line-height:1.4;">
              <div><strong>Tract:</strong> ${tractId}</div>
              <div><strong>Total (n):</strong> ${nTotal}</div>
              <div><strong>Target (n):</strong> ${nTarget}</div>
              <div><strong>Target (%):</strong> ${pctTarget}</div>
              <div><strong>Priority score:</strong> ${scoreVal}</div>
            </div>
          `;

          popup.setLngLat(e.lngLat).setHTML(html).addTo(map);
        };

        const onLeave = () => {
          if (popup) popup.remove();
        };

        map.__twynHoverHandlers.move = onMove;
        map.__twynHoverHandlers.leave = onLeave;

        map.on("mousemove", "tracts-fill", onMove);
        map.on("mouseleave", "tracts-fill", onLeave);
      } catch (err) {
        console.error("Error loading bundle:", err);
      }
    }

    updateStateView();

    return () => {
      cancelled = true;
    };
  }, [selectedState]);

  // -------------------------------------------
  // Master Overlay Effect: Watches selections and updates map
  // -------------------------------------------
  useEffect(() => {
    overlayDefinitions.forEach((def) => {
      const selected = overlaySelections[def.key];
      manageOverlayLayer(def, selected);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlaySelections, clientId, selectedState]);
  
  // Clear overlays on state change
  useEffect(() => {
     // Clear cache when state changes to avoid stale data
     overlayCacheRef.current = {}; 
     setOverlaySelections({});
  }, [selectedState]);
  
  // -------------------------------------------------------
  // Generic Overlay Helpers
  // -------------------------------------------------------
  const handleOverlaySelect = (key, brand) => {
    setOverlaySelections((prev) => {
      const current = prev[key] || [];
      // "ALL" (or "All brands") is exclusive; selecting it clears specific brands
      if (brand === "All brands" || brand === "ALL") return { ...prev, [key]: ["All brands"] };
      
      const withoutAll = current.filter(b => b !== "All brands" && b !== "ALL");
      if (withoutAll.includes(brand)) return prev; // already selected
      
      return { ...prev, [key]: [...withoutAll, brand] };
    });
  };

  const handleOverlayRemove = (key, brand) => {
    setOverlaySelections((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((b) => b !== brand),
    }));
  };

  const handleOverlayClear = (key) => {
    setOverlaySelections((prev) => ({ ...prev, [key]: [] }));
  };

  // -------------------------------------------
  // Helper: update a single scenario by index
  // -------------------------------------------
  const updateScenario = (index, updater) => {
    setScenarios((prev) =>
      prev.map((sc, idx) => (idx === index ? updater(sc) : sc))
    );
  };

// -------------------------------------------
// Helper: display a given scenario on the map
// -------------------------------------------
const displayScenarioOnMap = (scenario, { animateExtrusions = false } = {}) => {
  if (!mapRef.current || !tractGeoJSONRef.current || !selectedState) {
    setScoreResponse(scenario?.scoreResponse || null);
    return;
  }

  const map = mapRef.current;

  if (scenario && scenario.scoreResponse) {
    setScoreResponse(scenario.scoreResponse);

    // Only animate extrusions if explicitly requested AND we're in 3D view
    const shouldAnimateHeights = animateExtrusions && is3DViewRef.current;

    if (is3DViewRef.current) {
      applyScoresToMap(scenario.scoreResponse, {
        animate: shouldAnimateHeights,
      });
    } else {
      applyScoresToMap(scenario.scoreResponse, { animate: false });
      apply2DStyle();
    }
  } else {
    // No scores yet: clear Twyns and reset to base "film"
    setScoreResponse(null);
    setTwynCount(0);
    twynCountRef.current = 0;

    if (map.getLayer("tracts-fill")) {
      map.setPaintProperty("tracts-fill", "fill-extrusion-color", "#ffffff");
      map.setPaintProperty("tracts-fill", "fill-extrusion-opacity", 0.15);
      map.setPaintProperty("tracts-fill", "fill-extrusion-height", 0);
      map.setPaintProperty("tracts-fill", "fill-extrusion-base", 0);
    }
  }
};

  // -------------------------------------------
  // Handlers for base + target controls
  // -------------------------------------------
  const handleStateChange = (e) => {
    const newState = e.target.value;
    setSelectedState(newState);
    // scenario scores cleared & map reset in the useEffect on selectedState
  };

  const handleDemoToggleOpen = (varName) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      demoOpen: {
        ...sc.demoOpen,
        [varName]: !sc.demoOpen[varName],
      },
    }));
  };

  const handleDemoValueChange = (varName, code) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => {
      const cur = sc.demographicSelections[varName] || [];
      const exists = cur.includes(code);
      const nextValues = exists ? cur.filter((c) => c !== code) : [...cur, code];
      return {
        ...sc,
        demographicSelections: {
          ...sc.demographicSelections,
          [varName]: nextValues,
        },
      };
    });
  };
  
  const handleClearDemoSelection = (varName) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      demographicSelections: {
        ...sc.demographicSelections,
        [varName]: [],
      },
    }));
  };
  
  const handleToggleTargetPackage = (pkgName) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      targetPackageOpen: {
        ...(sc.targetPackageOpen || {}),
        [pkgName]: !(sc.targetPackageOpen || {})[pkgName],
      },
    }));
  };

  // ---------------------------------------------------------------------------
  // NEW: Generic Handler for Composite Variables (Heating, Water Heater, Heat Pumps)
  // ---------------------------------------------------------------------------
  const handleCompositeChange = (parentVar, childKey, code) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => {
      const currentSelections = sc.compositeSelections || {};
      const currentVal = currentSelections[childKey];

      // Toggle logic: if clicking same value, clear it; otherwise set it
      const newValue = currentVal === code ? null : code;

      const nextSelections = {
        ...currentSelections,
        [childKey]: newValue,
      };

      // --- SPECIAL LOGIC: Heat Pump Gating ---
      // If we are changing "Heat Pump Eligibility"...
      if (parentVar === "heat_pumps" && childKey === "hp_status") {
        // ...and the new status is NOT "Has Heat Pump" (and not null), clear the units.
        if (newValue && newValue !== "already_has_hp") {
          delete nextSelections.n_ductless_heatpumps;
        }
      }
      // ---------------------------------------

      // Check if ANY child in this parent group has a value
      const parentConfig = compositeConfig[parentVar] || [];
      const hasAnySelection = parentConfig.some(
        (child) => nextSelections[child.key]
      );
      const selectedTargetVars = sc.selectedTargetVars || [];

      if (
        hasAnySelection &&
        !canSelectTargetVariable(
          isDemoClient,
          selectedTargetVars,
          parentVar
        )
      ) {
        return sc;
      }

      return {
        ...sc,
        selectedTargetVars: hasAnySelection
          ? [...new Set([...selectedTargetVars, parentVar])]
          : selectedTargetVars.filter((variable) => variable !== parentVar),
        compositeSelections: nextSelections,
      };
    });
  };
  
  const handleSelectTargetVar = (varName) => {
      const idx = selectedScenarioIndex;
      updateScenario(idx, (sc) => {
        const currentOpen =
          sc.targetOpen && typeof sc.targetOpen[varName] === "boolean"
            ? sc.targetOpen[varName]
            : false;

        const newTargetOpen = { ...(sc.targetOpen || {}) };

        // Close everything else
        Object.keys(newTargetOpen).forEach((k) => {
          newTargetOpen[k] = false;
        });

        // Toggle this one
        newTargetOpen[varName] = !currentOpen;

        // Opening a group does not alter its current selection.

        return {
          ...sc,
          targetOpen: newTargetOpen,
        };
      });
    };

  const handleClearTargetSelection = (varName) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => {
      const targetValues = { ...(sc.targetValues || {}) };
      delete targetValues[varName];

      const compositeSelections = { ...(sc.compositeSelections || {}) };
      (compositeConfig[varName] || []).forEach((child) => {
        delete compositeSelections[child.key];
      });

      const next = {
        ...sc,
        selectedTargetVars: (sc.selectedTargetVars || []).filter(
          (variable) => variable !== varName
        ),
        targetValues,
        compositeSelections,
      };

      if (varName === "disability_status") next.selectedDisabilityChildren = [];
      if (varName === "hins_status") {
        next.selectedHinsClass = [];
        next.selectedHinsSources = [];
      }
      if (varName === "attic") next.selectedAtticChildren = [];

      return next;
    });
  };

  const handleTargetBaseValueChange = (varName, code, isMulti = false) => {
    const idx = selectedScenarioIndex;

    updateScenario(idx, (sc) => {
      const selectedTargetVars = sc.selectedTargetVars || [];
      const targetValues = { ...(sc.targetValues || {}) };

      if (
        !canSelectTargetVariable(
          isDemoClient,
          selectedTargetVars,
          varName
        )
      ) {
        return sc;
      }

      // MULTI-SELECT (e.g., race w/ many options)
      if (isMulti) {
        const current = Array.isArray(targetValues[varName])
          ? targetValues[varName]
          : [];
        const exists = current.includes(code);
        const updatedValues = exists
          ? current.filter((v) => v !== code)
          : [...current, code];

        if (updatedValues.length === 0) {
          delete targetValues[varName];
          return {
            ...sc,
            selectedTargetVars: selectedTargetVars.filter(
              (variable) => variable !== varName
            ),
            targetValues,
          };
        }

        return {
          ...sc,
          selectedTargetVars: [...new Set([...selectedTargetVars, varName])],
          targetValues: { ...targetValues, [varName]: updatedValues },
        };
      }

      // SINGLE-SELECT (2 response options)
      let next = {
        ...sc,
        selectedTargetVars: [...new Set([...selectedTargetVars, varName])],
        targetValues: { ...targetValues, [varName]: code },
      };

      // special reset logic
      if (varName === "disability_status" && code !== "with_disability") {
        next.selectedDisabilityChildren = [];
      }

      if (varName === "hins_status" && code !== "yes") {
        next.selectedHinsClass = [];
        next.selectedHinsSources = [];
      }

      if (varName === "attic" && code !== "yes") {
        next.selectedAtticChildren = [];
      }

      return next;

    });
  };

  const toggleFromList = (curList, value) =>
    curList.includes(value)
      ? curList.filter((v) => v !== value)
      : [...curList, value];

  const handleToggleDisabilityChild = (childVar) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      selectedDisabilityChildren: toggleFromList(
        sc.selectedDisabilityChildren || [],
        childVar
      ),
    }));
  };

  const handleToggleHinsClass = (childVar) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      selectedHinsClass: toggleFromList(sc.selectedHinsClass || [], childVar),
    }));
  };

  const handleToggleHinsSource = (childVar) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      selectedHinsSources: toggleFromList(
        sc.selectedHinsSources || [],
        childVar
      ),
    }));
  };

  const handleFinAtticChange = (code) => {
    const idx = selectedScenarioIndex;
    updateScenario(idx, (sc) => ({
      ...sc,
      selectedAtticChildren: [code],
    }));
  };

  const handleExportImage = async () => {
    if (!isDemoClient) return;

    if (!selectedState || !config) {
      window.alert("Please select a state and run 'Find My Twyns' first.");
      return;
    }

    const map = mapRef.current;
    if (!map) {
      window.alert("Map is not ready yet.");
      return;
    }

    // Scenarios that actually have results
    const scenariosWithResults = scenarios.filter(
      (sc) =>
        sc.scoreResponse &&
        Array.isArray(sc.scoreResponse.results) &&
        sc.scoreResponse.results.length > 0
    );

    if (scenariosWithResults.length === 0) {
      window.alert("No Twyn results found to export. Run 'Find My Twyns' first.");
      return;
    }

    const zip = new JSZip();
    const stateName = stateNameByFips[selectedState] || selectedState || "US";

    // Loop over *all* scenarios so numbering matches UI (1, 2, 3)
    for (let idx = 0; idx < scenarios.length; idx++) {
      const sc = scenarios[idx];
      const resp = sc.scoreResponse;

      if (
        !resp ||
        !Array.isArray(resp.results) ||
        resp.results.length === 0
      ) {
        continue; // skip empty scenarios
      }

      const scenarioNumber = idx + 1;
      const scenarioTwynCount = resp.results.reduce((sum, result) => {
        const count = typeof result.n_target === "number" ? result.n_target : 0;
        return sum + count;
      }, 0);

      // Show this scenario on the map without re-animation
      displayScenarioOnMap(sc, { animateExtrusions: false });

      // Wait for map to finish rendering, then capture the map and add the
      // scenario summary React overlay that Mapbox cannot include itself.
      const dataURL = await new Promise((resolve) => {
        map.once("idle", () => {
          const canvas = map.getCanvas();
          if (!canvas) {
            resolve(null);
            return;
          }
          resolve(
            exportMapWithScenarioSummary({
              mapCanvas: canvas,
              mapRect: map.getContainer().getBoundingClientRect(),
              panelRect: twynPanelRef.current?.getBoundingClientRect(),
              twynCount: scenarioTwynCount,
              scenarioNumber,
              scenarioDescription: describeScenario(sc),
            })
          );
        });
        map.triggerRepaint();
      });

      if (!dataURL) continue;

      const base64Data = dataURL.split(",")[1];
      const filename = `Name-${clientId || "client"}-${stateName}-Scenario${scenarioNumber}.png`;

      zip.file(filename, base64Data, { base64: true });
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const zipName = `Name-${clientId || "client"}-${stateName}-ScenarioImages.zip`;
    saveAs(zipBlob, zipName);
  };

  const handleExportData = async () => {
    if (!isDemoClient) return;

    if (!selectedState || !config) {
      window.alert("Please select a state and run 'Find My Twyns' first.");
      return;
    }

    const scenariosWithResults = scenarios.filter(
      (sc) =>
        sc.scoreResponse &&
        Array.isArray(sc.scoreResponse.results) &&
        sc.scoreResponse.results.length > 0
    );

    if (scenariosWithResults.length === 0) {
      window.alert("No Twyn results found to export. Run 'Find My Twyns' first.");
      return;
    }

    const zip = new JSZip();
    const stateName = stateNameByFips[selectedState] || selectedState || "US";

    scenarios.forEach((sc, idx) => {
      const resp = sc.scoreResponse;
      if (
        !resp ||
        !Array.isArray(resp.results) ||
        resp.results.length === 0
      ) {
        return; // skip empty scenarios
      }

      const scenarioNumber = idx + 1;
      const scenarioDesc = describeScenario(sc);

      const header = [
        "state_fips",
        "census_tract",
        "n_total",
        "n_target",
        "pct_target",
        "priority_score_100",
        "scenario_number",
        "scenario_description",
      ].join(",");

      const sortedResults = [...resp.results].sort((a, b) => {
        const av =
          typeof a.priority_score_100 === "number"
            ? a.priority_score_100
            : -Infinity;
        const bv =
          typeof b.priority_score_100 === "number"
            ? b.priority_score_100
            : -Infinity;
        return bv - av; // highest score first
      });

      const rows = sortedResults.map((r) => {
        const tract = r.census_tract ?? "";
        const nTotal = r.n_total ?? "";
        const nTarget = r.n_target ?? "";
        const pctTarget =
          typeof r.pct_target === "number" ? r.pct_target : "";
        const priority =
          typeof r.priority_score_100 === "number"
            ? r.priority_score_100
            : "";

        const safeDesc = `"${String(scenarioDesc).replace(/"/g, '""')}"`;

        return [
          selectedState,
          tract,
          nTotal,
          nTarget,
          pctTarget,
          priority,
          scenarioNumber,
          safeDesc,
        ].join(",");
      });

      const csvContent = [header, ...rows].join("\n");
      const filename = `Name-${clientId || "client"}-${stateName}-Scenario${scenarioNumber}.csv`;
      zip.file(filename, csvContent);
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const zipName = `Name-${clientId || "client"}-${stateName}-ScenarioData.zip`;
    saveAs(zipBlob, zipName);
  };

  // -------------------------------------------
  // Apply /scores response to the map (with optional animation)
  // -------------------------------------------
  const applyScoresToMap = (resp, { animate = true } = {}) => {
    if (!mapRef.current || !tractGeoJSONRef.current) return;

    const map = mapRef.current;

    // Collect all positive pct_target values
    const pctValues = (resp.results || [])
      .map((r) => (typeof r.pct_target === "number" ? r.pct_target : 0))
      .filter((v) => v > 0);

    // Max log for normalization (avoid /0)
    const maxLog = pctValues.length ? Math.log(Math.max(...pctValues)) : 1;

    const scoreMap = {};
    (resp.results || []).forEach((r) => {
      scoreMap[String(r.census_tract)] = {
        priority_score_100: r.priority_score_100,
        n_total: r.n_total,
        n_target: r.n_target,
        pct_target: r.pct_target,
      };
    });

    const original = tractGeoJSONRef.current;
    const updated = {
      ...original,
      features: original.features.map((f) => {
        const props = f.properties || {};
        const tractId =
          props.census_tract || props.GEOID || props.geoid || f.id || null;

        const metrics =
          tractId != null && scoreMap[String(tractId)]
            ? scoreMap[String(tractId)]
            : {
                priority_score_100: 0,
                n_total: null,
                n_target: null,
                pct_target: null,
              };

        return {
          ...f,
          properties: {
            ...props,
            ...metrics,
          },
        };
      }),
    };

    tractGeoJSONRef.current = updated;

    const src = map.getSource("tracts");
    if (src && src.setData) {
      src.setData(updated);
    }

    if (map.getLayer("tracts-fill")) {
    // Log-scaled 0–1 value based on pct_target
    const pctLogExpression = pctValues.length
      ? [
          "/",
          [
            "ln",
            [
              "max",
              [
                "to-number",
                ["coalesce", ["get", "pct_target"], 0]
              ],
              1e-6 // avoid ln(0)
            ]
          ],
          maxLog
        ]
      : ["coalesce", ["to-number", ["get", "pct_target"]], 0];

      map.setPaintProperty("tracts-fill", "fill-extrusion-color", [
        "interpolate",
        ["linear"],
        pctLogExpression,  // <--- use log-based 0–1 scale
        0.0, "#020024",
        0.25, "#090979",
        0.5, "#064EA6",
        0.75, "#00D4FF",
        1.0, "#09feff",
      ]);

      const currentOpacity = opacityRef.current ?? 0.85; // <-- NEW
      map.setPaintProperty("tracts-fill", "fill-extrusion-opacity", currentOpacity);

      const HEIGHT_SCALE = 10;
      const HEIGHT_MAX = 80000;

      const fullHeightExpression = [
        "min",
        ["*", ["to-number", ["coalesce", ["get", "n_target"], 0]], HEIGHT_SCALE],
        HEIGHT_MAX,
      ];

      map.setPaintProperty("tracts-fill", "fill-extrusion-base", 0);

      if (!animate) {
        map.setPaintProperty(
          "tracts-fill",
          "fill-extrusion-height",
          fullHeightExpression
        );
        return;
      }

      // Animated path (for Find My Twyns)
      map.setPaintProperty("tracts-fill", "fill-extrusion-height", 0);

      const DURATION = 1000;
      const startTime = performance.now();

      const animateHeights = (now) => {
        const t = Math.min((now - startTime) / DURATION, 1);
        const eased = t * t;

        map.setPaintProperty("tracts-fill", "fill-extrusion-height", [
          "*",
          eased,
          fullHeightExpression,
        ]);

        if (t < 1) {
          requestAnimationFrame(animateHeights);
        }
      };

      requestAnimationFrame(animateHeights);
    }
  };

  // -------------------------------------------
  // 2D styling: flat, colored by priority_score_100
  // -------------------------------------------
  const apply2DStyle = () => {
    const map = mapRef.current;
    if (!map || !map.getLayer("tracts-fill")) return;

    map.setPaintProperty("tracts-fill", "fill-extrusion-height", 0);
    map.setPaintProperty("tracts-fill", "fill-extrusion-base", 0);
    const currentOpacity = opacityRef.current ?? 0.85; // <-- NEW
    map.setPaintProperty("tracts-fill", "fill-extrusion-opacity", currentOpacity);

    const priorityExpression = [
      "coalesce",
      ["to-number", ["get", "priority_score_100"]],
      0,
    ];

    map.setPaintProperty("tracts-fill", "fill-extrusion-color", [
      "interpolate",
      ["linear"],
      priorityExpression,
      0,
      "#020024",
      25,
      "#090979",
      50,
      "#064EA6",
      75,
      "#00D4FF",
      100,
      "#09feff",
    ]);
  };

  // -------------------------------------------
  // Compute Twyn scores for ALL scenarios
  // -------------------------------------------
  const handleComputeScores = async () => {
    if (!config || !selectedState || scenarios.length === 0) return;

    setIsComputing(true);
    setComputeError(null);

    try {
      const updated = [];
      let activeResp = null;

      for (let i = 0; i < scenarios.length; i++) {
        const sc = scenarios[i];

        const demoFilters = Object.entries(sc.demographicSelections || {})
          .filter(([, vals]) => vals && vals.length > 0)
          .map(([variable, values]) => ({ variable, values }));

        const targetReqs = (sc.selectedTargetVars || [])
          .map((varName) => {
            const compositeDef = compositeConfig[varName];
            const targetValue = sc.targetValues?.[varName];

            if (compositeDef) {
              const targetReq = { variable: varName, values: [] };
              compositeDef.forEach((child) => {
                const value = sc.compositeSelections?.[child.key];
                if (value) targetReq[child.key] = [value];
              });
              return Object.keys(targetReq).length > 2 ? targetReq : null;
            }

            if (!targetValue) return null;

            const targetReq = {
              variable: varName,
              values: Array.isArray(targetValue) ? targetValue : [targetValue],
            };

            if (
              varName === "disability_status" &&
              targetValue === "with_disability" &&
              (sc.selectedDisabilityChildren || []).length > 0
            ) {
              targetReq.disability_types = sc.selectedDisabilityChildren;
            }

            if (varName === "hins_status" && targetValue === "yes") {
              if ((sc.selectedHinsClass || []).length > 0) {
                targetReq.hins_class = sc.selectedHinsClass;
              }
              if ((sc.selectedHinsSources || []).length > 0) {
                targetReq.hins_sources = sc.selectedHinsSources;
              }
            }

            if (
              varName === "attic" &&
              targetValue === "yes" &&
              (sc.selectedAtticChildren || []).length > 0
            ) {
              targetReq.fin_attic = sc.selectedAtticChildren;
            }

            return targetReq;
          })
          .filter(Boolean);

        const payload = {
          client_id: clientId,
          state_fips: selectedState,
          demographics: demoFilters,
          targets: targetReqs,
        };

        const resp = await getScores(payload);
        const scWithResp = { ...sc, scoreResponse: resp };
        updated.push(scWithResp);

        if (i === selectedScenarioIndex) {
          activeResp = resp;
        }
      }

      setScenarios(updated);

      const scenarioToDisplay =
        activeResp != null
          ? { ...updated[selectedScenarioIndex], scoreResponse: activeResp }
          : updated[selectedScenarioIndex];

      // Show results on the map with 3D grow animation
      displayScenarioOnMap(scenarioToDisplay, { animateExtrusions: true });

      // Trigger the Twyn count animation ONLY after Find My Twyns runs
      setShouldAnimateTwyns(true);

    } catch (err) {
      console.error(err);
      setComputeError(err.message || "Error computing scores");
    } finally {
      setIsComputing(false);
    }
  };

  // Group target variables by package (from backend config)
  const getTargetsByPackage = () => {
    const pkgs = config?.allowed_target_packages;

    // Preferred: backend sends { "Insurance": ["hins_status", ...], ... }
    if (pkgs && !Array.isArray(pkgs) && typeof pkgs === "object") {
      return pkgs;
    }

    // Fallback: backend sends ["Insurance","Housing",...]
    // (cannot infer membership, so show all vars under one bucket)
    return { Targets: config?.allowed_targets || [] };
  };

  const isYes = (v) => v === "yes" || (Array.isArray(v) && v.includes("yes"));

  const targetLabel = (varName) => targetVarLabels[varName] || varName;
  
  const scenarioDescription = describeScenario(activeScenario);

  // Build a natural-language description of the active scenario
  function describeScenario(sc) {
    if (!sc) return "";

    const parts = [];
    const demoParts = []; // Fixes the 'demoParts' is not defined error

    // 1. DEMOGRAPHICS: Follow config order for variables AND levelsMap order for values
    (config?.allowed_demographics || []).forEach((varName) => {
      const selectedCodes = sc.demographicSelections?.[varName] || [];
      if (selectedCodes.length > 0) {
        const levelDefs = levelsMap[varName] || {};
        
        // Iterate through levelsMap keys to ensure values appear in side-panel order
        const orderedLabels = Object.keys(levelDefs)
          .filter((code) => selectedCodes.includes(code))
          .map((code) => levelDefs[code]);

        if (orderedLabels.length > 0) {
          demoParts.push(
            `${demographicVarLabels[varName] || varName} = ${orderedLabels.join(", ")}`
          );
        }
      }
    });
    
    if (demoParts.length > 0) {
      parts.push(demoParts.join("; "));
    }

    // 2. TARGETS: Follow config order and include every selected target.
    const targetParts = [];
    (config?.allowed_targets || []).forEach((varName) => {
      if (!(sc.selectedTargetVars || []).includes(varName)) return;

      const varLabel = targetVarLabels[varName] || varName;
      const compositeDef = compositeConfig[varName];
      const targetValue = sc.targetValues?.[varName];
      let targetPart = "";

      if (compositeDef) {
        // --- NEW: Description for Composites ---
        const subParts = [];
        compositeDef.forEach(child => {
          const val = sc.compositeSelections?.[child.key];
          if (val) {
            const valLabel = levelsMap[child.key]?.[val] || val;
            // Short label logic (optional cleanup)
            const shortLabel = child.label
              .replace("Water Heater ", "")
              .replace("Heating ", "")
              .replace("Heat Pump ", "");
            subParts.push(`${shortLabel}: ${valLabel}`);
          }
        });
        
        if (subParts.length > 0) {
          targetPart = `${varLabel} (${subParts.join(", ")})`;
        } else {
          targetPart = varLabel; 
        }
      } 
      // --- EXISTING: Standard Description Logic ---
      else if (targetValue) {
          const levelDefs = levelsMap[varName] || {};
          const selectedCodes = Array.isArray(targetValue) ? targetValue : [targetValue];
          
          const orderedLabels = Object.keys(levelDefs)
            .filter((code) => selectedCodes.includes(code))
            .map((code) => levelDefs[code]);

          targetPart = `${varLabel} = ${orderedLabels.join(", ")}`;

        // Disability sub-category logic
        if (varName === "disability_status" && targetValue === "with_disability") {
          const selected = sc.selectedDisabilityChildren || [];
          const ordered = Object.keys(disabilityChildVars)
            .filter(k => selected.includes(k))
            .map(k => disabilityChildVars[k]);
          if (ordered.length) targetPart += ` (types: ${ordered.join(", ")})`;
        }

        // Health insurance sub-category logic
        if (varName === "hins_status" && targetValue === "yes") {
          const selectedClass = sc.selectedHinsClass || [];
          const selectedSources = sc.selectedHinsSources || [];

          const orderedClass = Object.keys(hinsClassVars)
            .filter(k => selectedClass.includes(k))
            .map(k => hinsClassVars[k]);

          const orderedSources = Object.keys(hinsSourceVars)
            .filter(k => selectedSources.includes(k))
            .map(k => hinsSourceVars[k]);

          const insuranceDetails = [
            ...orderedClass,
            ...orderedSources,
          ];

          if (insuranceDetails.length) {
            targetPart += ` (${insuranceDetails.join(", ")})`;
          }
        }

        // Attic child refinement logic
        if (varName === "attic" && targetValue === "yes") {
          const selected = sc.selectedAtticChildren || [];
          const ordered = Object.keys(levelsMap.fin_attic || {})
            .filter((code) => selected.includes(code))
            .map((code) => levelsMap.fin_attic[code]);

          if (ordered.length) {
            targetPart += ` (${ordered.join(", ")})`;
          }
        }
      }

      if (targetPart) targetParts.push(targetPart);
    });

    if (targetParts.length) parts.push(targetParts.join("; "));
    return parts.length ? parts.join("; ") : "No filters selected";
  }

    // Twyn count animation (based on active scoreResponse)
    useEffect(() => {
      // If no scores, reset to zero
      if (!scoreResponse || !Array.isArray(scoreResponse.results)) {
        setTwynCount(0);
        twynCountRef.current = 0;
        return;
      }

      const targetTotal = scoreResponse.results.reduce((sum, r) => {
        const n = typeof r.n_target === "number" ? r.n_target : 0;
        return sum + n;
      }, 0);

      // If we're NOT animating (e.g., switching scenarios), just set the value directly
      if (!shouldAnimateTwyns) {
        setTwynCount(targetTotal);
        twynCountRef.current = targetTotal;
        return;
      }

      // Otherwise, run the count-up animation (used when Find My Twyns is clicked)
      const start = twynCountRef.current || 0;
      const end = targetTotal;
      const duration = 800; // ms
      const startTime = performance.now();

      const animate = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const value = Math.round(start + (end - start) * progress);
        setTwynCount(value);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          twynCountRef.current = end;
          // Reset so future scenario switches don't re-run the animation
          setShouldAnimateTwyns(false);
        }
      };

      requestAnimationFrame(animate);
    }, [scoreResponse, shouldAnimateTwyns]);

    // Keep the search bar (geocoder) directly beneath the Twyn count box
    useEffect(() => {
      const map = mapRef.current;
      const geocoderEl = geocoderContainerRef.current;
      const twynEl = twynPanelRef.current;

      if (!map || !geocoderEl || !twynEl) return;

      const mapRect = map.getContainer().getBoundingClientRect();
      const twynRect = twynEl.getBoundingClientRect();

      // Match geocoder width to the rendered Twyn box width
      const twynWidth = twynRect.width;
      geocoderEl.style.width = `${twynWidth}px`;
      geocoderEl.style.maxWidth = `${twynWidth}px`;
      geocoderEl.style.boxSizing = "border-box";

      // Place geocoder just below the Twyn panel, with an 8px gap
      const newTop = twynRect.bottom - mapRect.top + 8;

      geocoderEl.style.position = "absolute";
      geocoderEl.style.top = `${newTop}px`;
      geocoderEl.style.right = "4.5rem";
      geocoderEl.style.left = "auto";
    }, [
      selectedState,
      twynCount,
      selectedScenarioIndex,
      scenarioDescription,
    ]);

  // -------------------------------------------
  // Scenario nav: add / remove / select
  // -------------------------------------------
  const handleSaveArchetype = () => {
    if (!activeScenario) return;

    setSavedArchetype({
      sourceScenarioId: activeScenario.scenarioId,
      selections: copyScenarioSelections(activeScenario),
    });
  };

  const handleApplyArchetype = () => {
    if (!savedArchetype || !activeScenario) return;

    const updatedScenario = {
      ...activeScenario,
      ...copyScenarioSelections(savedArchetype.selections),
      scoreResponse: null,
    };

    setScenarios((prev) =>
      prev.map((scenario, index) =>
        index === selectedScenarioIndex ? updatedScenario : scenario
      )
    );

    // The copied filters have not been scored yet, so clear stale results.
    displayScenarioOnMap(updatedScenario);
  };

  const handleAddScenario = () => {
    if (!config || !canAddAnotherScenario) return;

    const newScenario = makeEmptyScenario(config);
    const newScenarios = [...scenarios, newScenario];
    setScenarios(newScenarios);

    const newIndex = newScenarios.length - 1;
    setSelectedScenarioIndex(newIndex);

    // New scenario → no scores yet → clear map/Twyns
    displayScenarioOnMap(newScenario);
  };

  const handleRemoveScenario = () => {
    // 1. Check if only one scenario exists. If so, perform a reset (clear) operation.
    if (scenarios.length === 1) {
      if (!config) return; // Ensure config is loaded

      const scenarioToReset = scenarios[0];
      
      // Use makeEmptyScenario to create a scenario with the correct internal structure.
      let newResetScenario = makeEmptyScenario(config);

      // Overwrite the necessary fields to preserve them:
      newResetScenario = {
        ...newResetScenario, // Keeps all the correctly initialized defaults (e.g., selections: { ageband: [] })
        name: scenarioToReset.name,
        stateSelection: scenarioToReset.stateSelection,
        
        // Ensure other non-selection/score properties are reset
        scoreLayer: null,
        apiConfig: null,
        isLoading: false,
        errorMessage: null,
        opacity: 0.75,
        styleIndex: 0,
      };

      // Update the state with the single, reset scenario
      setScenarios([newResetScenario]);
      setSelectedScenarioIndex(0);

      // Update the map to show the reset scenario (clears map/Twyns)
      displayScenarioOnMap(newResetScenario);

      return; // Exit the function after reset
    }


    // 2. Original logic for scenarios.length > 1 (standard removal)
    const idx = selectedScenarioIndex;
    const newScenarios = scenarios.filter((_, i) => i !== idx);

    let newIndex = selectedScenarioIndex;
    if (newIndex === 0) {
      newIndex = 0;
    } else if (newIndex >= newScenarios.length) {
      newIndex = newScenarios.length - 1;
    }

    setScenarios(newScenarios);
    setSelectedScenarioIndex(newIndex);

    const sc = newScenarios[newIndex];
    displayScenarioOnMap(sc);
  };

  const handleSelectScenario = (idx) => {
    setSelectedScenarioIndex(idx);
    const sc = scenarios[idx];
    displayScenarioOnMap(sc);
  };

  // -------------------------------------------
  // Render
  // -------------------------------------------
  return (
    <div
      style={{
        position: "relative",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
      }}
    >
      {/* MAP FULLSCREEN */}
      <div ref={mapContainerRef} style={{ position: "absolute", inset: 0 }} />

      {!mapboxgl.accessToken && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            padding: "10px",
            background: "rgba(255,255,255,0.9)",
            color: "red",
            top: 10,
            left: 10,
          }}
        >
          ERROR: REACT_APP_MAPBOX_ACCESS_TOKEN is not set.
        </div>
      )}

      {/* SCENARIO SIDEBAR (LEFT OF MAIN PANEL) */}
      {status === "loaded" && config && scenarios.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "19.9rem",
            left: "0.5rem",
            transform: "translateY(-50%)",
            zIndex: 11,
            background: "rgba(2, 6, 23, 0.96)",
            borderRadius: "999px",
            border: "1px solid #1f2937",
            boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
            padding: "0.5rem 0.4rem",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.4rem",
            maxHeight: "calc(100vh - 2rem)",
            overflowY: "auto",
          }}
        >
          {/* +/- buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.3rem",
              marginBottom: "0.2rem",
            }}
          >
            <button
              type="button"
              onClick={handleAddScenario}
              disabled={isScenarioAddDisabled}
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "999px",
                border: "1px solid #4b5563",
                background: isScenarioAddDisabled
                  ? "#020617"
                  : "rgba(31,41,55,0.85)",
                color: "#e5e7eb",
                cursor: isScenarioAddDisabled ? "not-allowed" : "pointer",
                fontSize: "0.9rem",
                lineHeight: 1,
              }}
              title={
                isComputing
                  ? "Processing..."
                  : canAddAnotherScenario
                  ? "Add scenario"
                  : "Maximum of 5 scenarios reached"
              }
            >
              +
            </button>
            <button
              type="button"
              onClick={handleRemoveScenario}
              // Disable removal while computing to prevent state corruption
              disabled={isComputing}
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "999px",
                border: "1px solid #4b5563",
                background: isComputing ? "#020617" : "rgba(31,41,55,0.85)", 
                color: "#e5e7eb",
                cursor: isComputing ? "not-allowed" : "pointer",
                fontSize: "0.9rem",
                lineHeight: 1,
              }}
              title={
                isComputing 
                  ? "Processing..." 
                  : scenarios.length === 1
                    ? "Reset current scenario (keep state selection)"
                    : "Remove current scenario"
              }
            >
              –
            </button>
          </div>

          {/* Scenario buttons 1 / 2 / 3 */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.25rem",
            }}
          >
            {scenarios.map((scenario, idx) => {
              const isActive = idx === selectedScenarioIndex;
              return (
                <button
                  key={scenario.scenarioId}
                  type="button"
                  disabled={isComputing}
                  onClick={() => handleSelectScenario(idx)}
                  style={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "999px",
                    border: isActive
                      ? "1px solid #22D4F0"
                      : "1px solid #4b5563",
                    background: isActive
                      ? "rgba(34,212,240,0.25)"
                      : "rgba(15,23,42,0.9)",
                    color: "#e5e7eb",
                    cursor: isComputing ? "not-allowed" : "pointer",
                    opacity: isComputing && !isActive ? 0.5 : 1,
                    fontSize: "0.8rem",
                    fontWeight: isActive ? 700 : 500,
                  }}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* PANEL OVERLAY */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 15,
          margin: "1rem",
          width: "320px",
          maxHeight: "calc(100vh - 2rem)",
          background: "rgba(2, 6, 23, 0.96)",
          color: "#e5e7eb",
          padding: "1.25rem",
          boxSizing: "border-box",
          borderRadius: "8px",
          border: "1px solid #1f2937",
          boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          zIndex: 10,
          overflow: "hidden", // Prevent the panel itself from scrolling
        }}
      >
        {/* --- FIXED HEADER SECTION --- */}
        <div style={{ flexShrink: 0 }}>
          {/* TOP TOOLBAR */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "1.5rem",
              position: "relative",
              gap: "1.15rem",
            }}
          >
            {/* Map style menu */}
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className={`icon-button ${
                  isMapMenuOpen ? "icon-button--open" : ""
                }`}
                onClick={() => {
                  setIsMapMenuOpen((prev) => !prev);
                  setIsInfoMenuOpen(false);
                  setIsDownloadMenuOpen(false);
                  setIsLogoutMenuOpen(false);
                  setActiveInfoItem(null);
                  setActiveDownloadItem(null);
                }}
              >
                <FiMap size={22} color="#e5e7eb" />
              </button>

              {isMapMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "135%",
                    right: "-100px",
                    background: "rgba(15,23,42,0.98)",
                    borderRadius: "999px",
                    border: "1px solid #4b5563",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
                    padding: "0.25rem 0.6rem",
                    zIndex: 20,
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  {[
                    { id: "dark", label: "Dark" },
                    { id: "light", label: "Light" },
                    { id: "satellite", label: "Satellite" },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setBaseMapStyle(opt.id)}
                      style={{
                        width: "auto",
                        textAlign: "center",
                        padding: "0.3rem 0.7rem",
                        background:
                          baseMapStyle === opt.id
                            ? "rgba(34,212,240,0.16)"
                            : "transparent",
                        borderRadius: "999px",
                        border: "none",
                        color: baseMapStyle === opt.id ? "#22D4F0" : "#e5e7eb",
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info / User Guide menu */}
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className={`icon-button ${
                  isInfoMenuOpen ? "icon-button--open" : ""
                }`}
                aria-label="View User Guide"
                title={
                  isDemoClient
                    ? "View User Guide"
                    : "View User Guide is available in Demo only"
                }
                disabled={!isDemoClient}
                onClick={() => {
                  if (!isDemoClient) return;
                  setIsInfoMenuOpen((prev) => {
                    const next = !prev;
                    if (!next) {
                      setActiveInfoItem(null);
                    }
                    return next;
                  });
                  setIsMapMenuOpen(false);
                  setIsDownloadMenuOpen(false);
                  setIsLogoutMenuOpen(false);
                  setActiveDownloadItem(null);
                }}
              >
                <FiInfo
                  size={22}
                  color={isDemoClient ? "#e5e7eb" : "#64748b"}
                />
              </button>

              {isDemoClient && isInfoMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "135%",
                    right: 0,
                    background: "rgba(15,23,42,0.98)",
                    borderRadius: "999px",
                    border: "1px solid #4b5563",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
                    padding: "0.25rem 0.75rem",
                    zIndex: 20,
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  <a
                    href={userGuidePdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setActiveInfoItem("userGuide")}
                    style={{
                      textDecoration: "none",
                      padding: "0.3rem 0.7rem",
                      borderRadius: "999px",
                      border: "none",
                      background:
                        activeInfoItem === "userGuide"
                          ? "rgba(34,212,240,0.16)"
                          : "transparent",
                      color:
                        activeInfoItem === "userGuide" ? "#22D4F0" : "#e5e7eb",
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    View User Guide
                  </a>
                </div>
              )}
            </div>

            {/* Download menu */}
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className={`icon-button ${
                  isDownloadMenuOpen ? "icon-button--open" : ""
                }`}
                aria-label="Export"
                title={
                  isDemoClient
                    ? "Export"
                    : "Export is available in Demo only"
                }
                disabled={!isDemoClient}
                onClick={() => {
                  if (!isDemoClient) return;
                  setIsDownloadMenuOpen((prev) => {
                    const next = !prev;
                    if (!next) {
                      setActiveDownloadItem(null);
                    }
                    return next;
                  });
                  setIsMapMenuOpen(false);
                  setIsInfoMenuOpen(false);
                  setIsLogoutMenuOpen(false);
                  setActiveInfoItem(null);
                }}
              >
                <FiDownload
                  size={22}
                  color={isDemoClient ? "#e5e7eb" : "#64748b"}
                />
              </button>

              {isDemoClient && isDownloadMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "135%",
                    right: 0,
                    background: "rgba(15,23,42,0.98)",
                    borderRadius: "999px",
                    border: "1px solid #4b5563",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
                    padding: "0.25rem 0.6rem",
                    zIndex: 20,
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      handleExportImage();
                      setActiveDownloadItem("image");
                    }}
                    style={{
                      padding: "0.3rem 0.7rem",
                      borderRadius: "999px",
                      border: "none",
                      background:
                        activeDownloadItem === "image"
                          ? "rgba(34,212,240,0.16)"
                          : "transparent",
                      color:
                        activeDownloadItem === "image" ? "#22D4F0" : "#e5e7eb",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Export Image
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleExportData();
                      setActiveDownloadItem("data");
                    }}
                    style={{
                      padding: "0.3rem 0.7rem",
                      borderRadius: "999px",
                      border: "none",
                      background:
                        activeDownloadItem === "data"
                          ? "rgba(34,212,240,0.16)"
                          : "transparent",
                      color:
                        activeDownloadItem === "data" ? "#22D4F0" : "#e5e7eb",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Export Data
                  </button>
                </div>
              )}
            </div>

            {/* Logout menu */}
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className={`icon-button ${
                  isLogoutMenuOpen ? "icon-button--open" : ""
                }`}
                title="Logout"
                onClick={() => {
                  setIsLogoutMenuOpen((prev) => !prev);
                  setIsMapMenuOpen(false);
                  setIsInfoMenuOpen(false);
                  setIsDownloadMenuOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiLogOut
                  size={22}
                  color="#e5e7eb"
                />
              </button>

              {isLogoutMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "135%",
                    right: 0,
                    background: "rgba(15,23,42,0.98)",
                    borderRadius: "999px",
                    border: "1px solid rgba(249, 115, 115, 0.4)",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                    padding: "0.25rem 0.6rem",
                    zIndex: 20,
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.5rem",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    style={{
                      background: "rgba(249, 115, 115, 0.15)",
                      border: "none",
                      padding: "0.3rem 1rem",
                      borderRadius: "999px",
                      color: "#f97373",
                      fontSize: "0.8rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.15s ease",
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = "rgba(249, 115, 115, 0.3)";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = "rgba(249, 115, 115, 0.15)";
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>


          {/* HEADER */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.1rem",
              marginBottom: "-0.75rem",
            }}
          >
            <img
              src={twynLogo}
              alt="Twyn logo"
              style={{
                width: 60,
                height: 60,
                marginLeft: "-16px",
                marginRight: "-5px",
                borderRadius: "6px",
                flexShrink: 0,
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: "0.4rem",
              }}
            >
              <h1
                style={{
                  margin: 0,
                  fontSize: "1.65rem",
                  fontWeight: 700,
                  color: "#22D4F0",
                  letterSpacing: "0.03em",
                }}
              >
                Twin
              </h1>

              <span
                style={{
                  color: "#ffffff",
                  fontSize: "1.55rem",
                  opacity: 0.9,
                }}
              >
                for {clientId}
              </span>

            </div>

          </div>


          {status === "loading" && <p>Loading client config…</p>}
          {status === "error" && (
            <p style={{ color: "#f97373" }}>Error: {error}</p>
          )}
        </div>

        {/* --- SCROLLABLE CONTENT SECTION (State, Base, Target, Overlays) --- */}
        {status === "loaded" && config && (
          <>
            {/* Custom scrollbar styles for this specific panel */}
            <style>{`
              .twyn-panel-scroll {
                scrollbar-width: thin;
                scrollbar-color: #4b5563 transparent;
              }
              .twyn-panel-scroll::-webkit-scrollbar {
                width: 6px;
              }
              .twyn-panel-scroll::-webkit-scrollbar-track {
                background: transparent;
              }
              .twyn-panel-scroll::-webkit-scrollbar-thumb {
                background-color: #374151;
                border-radius: 999px;
                border: 2px solid transparent;
                background-clip: content-box;
              }
              .twyn-panel-scroll::-webkit-scrollbar-thumb:hover {
                background-color: #6b7280;
              }
            `}</style>

            <div
              className="twyn-panel-scroll"
              style={{
                flex: 1, 
                overflowY: "auto", 
                minHeight: 0, 
                paddingRight: "2px", 
                display: "flex",
                flexDirection: "column",
                gap: "1rem", 
              }}
            >
              {/* State selection */}
              <section>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.35rem",
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#9ca3af",
                  }}
                >
                  State
                </label>
                <select
                  value={selectedState}
                  onChange={handleStateChange}
                  style={{
                    width: "100%",
                    padding: "0.45rem 0.5rem",
                    borderRadius: "6px",
                    border: "1px solid #4b5563",
                    backgroundColor: "#020617",
                    color: "#e5e7eb",
                    fontSize: "0.95rem",
                  }}
                >
                  <option value="">Select a state…</option>
                  {(config.allowed_states || []).map((fips) => {
                    const name = stateNameByFips[fips] || "Unknown";
                    return (
                      <option key={fips} value={fips}>
                        {name} ({fips})
                      </option>
                    );
                  })}
                </select>
              </section>

              {/* Archetype controls */}
              <section
                style={{
                  border: "1px solid #1f2937",
                  borderRadius: "6px",
                  padding: "0.6rem 0.75rem",
                  background: "rgba(15,23,42,0.55)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#9ca3af",
                      }}
                    >
                      Archetype
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "0.15rem" }}>
                      {savedArchetype
                        ? savedArchetypeSourceIndex >= 0
                          ? `Saved from Scenario ${savedArchetypeSourceIndex + 1}`
                          : "Saved archetype (source removed)"
                        : "Save filters to reuse across scenarios"}
                    </div>
                  </div>
                  {savedArchetype && (
                    <button
                      type="button"
                      onClick={() => setSavedArchetype(null)}
                      disabled={isComputing}
                      title="Clear saved archetype"
                      aria-label="Clear saved archetype"
                      style={{
                        border: "none",
                        background: "none",
                        color: "#9ca3af",
                        cursor: isComputing ? "not-allowed" : "pointer",
                        fontSize: "1rem",
                        lineHeight: 1,
                        padding: "0.1rem",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={handleSaveArchetype}
                    disabled={isComputing}
                    style={{
                      flex: 1,
                      padding: "0.35rem 0.45rem",
                      borderRadius: "5px",
                      border: "1px solid #4b5563",
                      background: "rgba(31,41,55,0.85)",
                      color: "#e5e7eb",
                      cursor: isComputing ? "not-allowed" : "pointer",
                      fontSize: "0.78rem",
                    }}
                  >
                    Save current
                  </button>
                  <button
                    type="button"
                    onClick={handleApplyArchetype}
                    disabled={!savedArchetype || isComputing}
                    title="Copy the saved archetype filters into this scenario"
                    style={{
                      flex: 1,
                      padding: "0.35rem 0.45rem",
                      borderRadius: "5px",
                      border: "1px solid #4b5563",
                      background:
                        savedArchetype && !isComputing
                          ? "rgba(34,212,240,0.16)"
                          : "#020617",
                      color: savedArchetype ? "#22D4F0" : "#6b7280",
                      cursor:
                        savedArchetype && !isComputing ? "pointer" : "not-allowed",
                      fontSize: "0.78rem",
                    }}
                  >
                    Apply here
                  </button>
                </div>
              </section>

              {/* Base variables */}
              <section>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "0.3rem",
                  }}
                >
                  <label
                    style={{
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      color: "#9ca3af",
                    }}
                  >
                    Base Variables
                  </label>
                </div>
                {config.allowed_demographics.length === 0 ? (
                  <p style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                    No demographic filters configured.
                  </p>
                ) : (
                  <div
                    className="twyn-panel-scroll" 
                    style={{
                      ...scrollBoxStyle,
                      opacity: selectedState ? 1 : 0.4,
                      pointerEvents: selectedState ? "auto" : "none",
                    }}
                  >
                    {config.allowed_demographics.map((varName) => (
                      <DemographicGroup
                        key={varName}
                        varName={varName}
                        labelMap={demographicVarLabels}
                        isOpen={!!demoOpen[varName]}
                        onToggle={() => handleDemoToggleOpen(varName)}
                        selectedValues={demographicSelections[varName] || []}
                        onChangeValue={handleDemoValueChange}
                        onClearSelection={() =>
                          handleClearDemoSelection(varName)
                        }
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Target variables (PACKAGED) */}
              <section>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.3rem",
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: "#9ca3af",
                  }}
                >
                  Target Variables
                </label>

                {(config.allowed_targets || []).length === 0 ? (
                  <p style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                    No target variables configured.
                  </p>
                ) : (
                  <div
                    className="twyn-panel-scroll" 
                    style={{
                      ...scrollBoxStyle,
                      opacity: selectedState ? 1 : 0.4,
                      pointerEvents: selectedState ? "auto" : "none",
                    }}
                  >
                    {Object.entries(getTargetsByPackage()).map(
                      ([pkgName, vars]) => {
                        const pkgPretty =
                          targetPackageLabels?.[pkgName] || pkgName;
                        const pkgIsOpen = !!targetPackageOpen[pkgName];

                        return (
                          <div
                            key={pkgName}
                            style={{ marginBottom: "0.75rem" }}
                          >
                            <button
                              type="button"
                              onClick={() => handleToggleTargetPackage(pkgName)}
                              style={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                margin: 0,
                                fontSize: "0.92rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                color: "#e5e7eb",
                              }}
                            >
                              {pkgIsOpen ? "▾ " : "▸ "}
                              {pkgPretty}
                            </button>

                            {pkgIsOpen && (
                              <div style={{ marginTop: "0.35rem" }}>
                                {(vars || []).map((varName) => {
                                  const prettyLabel = targetLabel(varName);
                                  const isOpen = !!targetOpen[varName];
                                  const isSelected =
                                    selectedTargetVars.includes(varName);
                                  const targetValue =
                                    activeScenario?.targetValues?.[varName] || null;

                                  // 1. Check for Composite Config (Heating, Water Heater, etc.)
                                  const compositeDef = compositeConfig[varName];

                                  const groupDisabled =
                                    !canSelectTargetVariable(
                                      isDemoClient,
                                      selectedTargetVars,
                                      varName
                                    );

                                  return (
                                    <div
                                      key={varName}
                                      style={{
                                        marginBottom: "0.6rem",
                                        opacity: groupDisabled ? 0.5 : 1,
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "0.35rem",
                                          marginLeft: "0.6rem",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleSelectTargetVar(varName)
                                          }
                                          disabled={groupDisabled}
                                          style={{
                                            border: "none",
                                            background: "none",
                                            padding: 0,
                                            margin: 0,
                                            fontSize: "0.9rem",
                                            fontWeight: 600,
                                            cursor: groupDisabled
                                              ? "not-allowed"
                                              : "pointer",
                                            color: "#e5e7eb",
                                          }}
                                          title={
                                            groupDisabled
                                              ? "Maximum of 2 target variables reached"
                                              : undefined
                                          }
                                        >
                                          {isOpen ? "▾ " : "▸ "} {prettyLabel}
                                        </button>

                                        {/* Clear 'X' - Only if THIS variable is selected */}
                                        {isSelected && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleClearTargetSelection(varName);
                                            }}
                                            style={{
                                              border: "none",
                                              background: "none",
                                              padding: 0,
                                              fontSize: "0.75rem",
                                              cursor: "pointer",
                                              color: "#9ca3af",
                                            }}
                                            title="Clear target selection"
                                          >
                                            ✕
                                          </button>
                                        )}
                                      </div>

                                      {isOpen && (
                                        <div
                                          style={{
                                            marginTop: "0.25rem",
                                            marginLeft: "2.0rem",
                                            fontSize: "0.85rem",
                                          }}
                                        >
                                          {/* PATH A: Composite Variables (Heating, Water Heater, Heat Pump) */}
                                          {compositeDef ? (
                                            <div>
                                              {compositeDef.map((child) => {
                                                const childVal = activeScenario.compositeSelections?.[child.key];

                                                // --- GATING LOGIC ---
                                                // Start with the group's disabled state
                                                let isChildDisabled = groupDisabled;
                                                
                                                // Specific rule: Disable "Number of Ductless Units" ONLY IF 
                                                // "Heat Pump Eligibility" is selected AND it is NOT "Has Heat Pump".
                                                if (child.key === "n_ductless_heatpumps") {
                                                  const hpStatus = activeScenario.compositeSelections?.hp_status;
                                                  if (hpStatus && hpStatus !== "already_has_hp") {
                                                    isChildDisabled = true;
                                                  }
                                                }

                                                return (
                                                  <div
                                                    key={child.key}
                                                    style={{
                                                      marginTop: "0.4rem",
                                                      // Gray it out if specifically disabled
                                                      opacity: isChildDisabled ? 0.5 : 1, 
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.85rem",
                                                        marginBottom: "0.2rem",
                                                      }}
                                                    >
                                                      {child.label}
                                                    </div>

                                                    {Object.entries(
                                                      levelsMap[child.key] || {}
                                                    ).map(([code, label]) => (
                                                      <label
                                                        key={code}
                                                        style={{
                                                          display: "block",
                                                          marginBottom: "0.2rem",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          name={`${child.key}-${selectedScenarioIndex}`}
                                                          checked={childVal === code}
                                                          // Use the specific child disabled state
                                                          disabled={isChildDisabled} 
                                                          onChange={() =>
                                                            handleCompositeChange(
                                                              varName,
                                                              child.key,
                                                              code
                                                            )
                                                          }
                                                          style={{
                                                            marginRight: "0.35rem",
                                                          }}
                                                        />
                                                        {label}
                                                      </label>
                                                    ))}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            /* PATH B: Standard Variables (Health Ins, Disability, etc.) */
                                            <div>
                                              {Object.entries(
                                                levelsMap[varName] || {}
                                              ).map(([code, label]) => {
                                                const levelDefs =
                                                  levelsMap[varName] || {};
                                                const forceSingleSelect = [
                                                  "insulated",
                                                  "main_ac_equp",
                                                  "roof_mat"
                                                ];
                                                const isMultiValueTarget =
                                                  Object.keys(levelDefs)
                                                    .length > 2 &&
                                                  !forceSingleSelect.includes(
                                                    varName
                                                  );

                                                return (
                                                  <label
                                                    key={code}
                                                    style={{
                                                      display: "block",
                                                      marginBottom: "0.2rem",
                                                    }}
                                                  >
                                                    {isMultiValueTarget ? (
                                                      <input
                                                        type="checkbox"
                                                        name={`target-${varName}-${code}`}
                                                        value={code}
                                                        checked={
                                                          isSelected &&
                                                          Array.isArray(
                                                            targetValue
                                                          )
                                                            ? targetValue.includes(
                                                                code
                                                              )
                                                            : false
                                                        }
                                                        disabled={groupDisabled}
                                                        onChange={() =>
                                                          handleTargetBaseValueChange(
                                                            varName,
                                                            code,
                                                            true
                                                          )
                                                        }
                                                        style={{
                                                          marginRight:
                                                            "0.35rem",
                                                        }}
                                                      />
                                                    ) : (
                                                      <input
                                                        type="radio"
                                                        name={`target-${varName}`}
                                                        value={code}
                                                        checked={
                                                          isSelected &&
                                                          targetValue === code
                                                        }
                                                        disabled={groupDisabled}
                                                        onChange={() =>
                                                          handleTargetBaseValueChange(
                                                            varName,
                                                            code,
                                                            false
                                                          )
                                                        }
                                                        style={{
                                                          marginRight:
                                                            "0.35rem",
                                                        }}
                                                      />
                                                    )}
                                                    {label}
                                                  </label>
                                                );
                                              })}

                                              {/* Sub-questions for Disability */}
                                              {varName ===
                                                "disability_status" &&
                                                targetValue ===
                                                  "with_disability" &&
                                                isSelected && (
                                                  <div
                                                    style={{
                                                      marginTop: "0.4rem",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.85rem",
                                                        marginBottom: "0.2rem",
                                                      }}
                                                    >
                                                      Disability Type
                                                    </div>
                                                    {Object.entries(
                                                      disabilityChildVars
                                                    ).map(
                                                      ([childVar, label]) => (
                                                        <label
                                                          key={childVar}
                                                          style={{
                                                            display: "block",
                                                            marginBottom:
                                                              "0.2rem",
                                                          }}
                                                        >
                                                          <input
                                                            type="checkbox"
                                                            checked={selectedDisabilityChildren.includes(
                                                              childVar
                                                            )}
                                                            disabled={
                                                              groupDisabled
                                                            }
                                                            onChange={() =>
                                                              handleToggleDisabilityChild(
                                                                childVar
                                                              )
                                                            }
                                                            style={{
                                                              marginRight:
                                                                "0.35rem",
                                                            }}
                                                          />
                                                          {label}
                                                        </label>
                                                      )
                                                    )}
                                                  </div>
                                                )}

                                              {/* Sub-questions for Health Insurance */}
                                              {varName === "hins_status" &&
                                                targetValue === "yes" &&
                                                isSelected && (
                                                  <>
                                                    <div
                                                      style={{
                                                        marginTop: "0.4rem",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          fontWeight: 600,
                                                          fontSize: "0.85rem",
                                                          marginBottom:
                                                            "0.2rem",
                                                        }}
                                                      >
                                                        Coverage Class
                                                      </div>
                                                      {Object.entries(
                                                        hinsClassVars
                                                      ).map(
                                                        ([childVar, label]) => (
                                                          <label
                                                            key={childVar}
                                                            style={{
                                                              display: "block",
                                                              marginBottom:
                                                                "0.2rem",
                                                            }}
                                                          >
                                                            <input
                                                              type="checkbox"
                                                              checked={selectedHinsClass.includes(
                                                                childVar
                                                              )}
                                                              disabled={
                                                                groupDisabled
                                                              }
                                                              onChange={() =>
                                                                handleToggleHinsClass(
                                                                  childVar
                                                                )
                                                              }
                                                              style={{
                                                                marginRight:
                                                                  "0.35rem",
                                                              }}
                                                            />
                                                            {label}
                                                          </label>
                                                        )
                                                      )}
                                                    </div>
                                                    <div
                                                      style={{
                                                        marginTop: "0.4rem",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          fontWeight: 600,
                                                          fontSize: "0.85rem",
                                                          marginBottom:
                                                            "0.2rem",
                                                        }}
                                                      >
                                                        Insurance Sources
                                                      </div>
                                                      {Object.entries(
                                                        hinsSourceVars
                                                      ).map(
                                                        ([childVar, label]) => (
                                                          <label
                                                            key={childVar}
                                                            style={{
                                                              display: "block",
                                                              marginBottom:
                                                                "0.2rem",
                                                            }}
                                                          >
                                                            <input
                                                              type="checkbox"
                                                              checked={selectedHinsSources.includes(
                                                                childVar
                                                              )}
                                                              disabled={
                                                                groupDisabled
                                                              }
                                                              onChange={() =>
                                                                handleToggleHinsSource(
                                                                  childVar
                                                                )
                                                              }
                                                              style={{
                                                                marginRight:
                                                                  "0.35rem",
                                                              }}
                                                            />
                                                            {label}
                                                          </label>
                                                        )
                                                      )}
                                                    </div>
                                                  </>
                                                )}
                                              
                                              {/* Sub-question for Attic */}
                                              {varName === "attic" &&
                                                targetValue === "yes" &&
                                                isSelected && (
                                                  <div
                                                    style={{
                                                      marginTop: "0.4rem",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        fontWeight: 600,
                                                        fontSize: "0.85rem",
                                                        marginBottom: "0.2rem",
                                                      }}
                                                    >
                                                      Attic Type
                                                    </div>

                                                    {Object.entries(levelsMap.fin_attic || {}).map(([code, label]) => (
                                                      <label
                                                        key={code}
                                                        style={{
                                                          display: "block",
                                                          marginBottom: "0.2rem",
                                                        }}
                                                      >
                                                        <input
                                                          type="radio"
                                                          name={`fin-attic-${selectedScenarioIndex}`}
                                                          checked={selectedAtticChildren.includes(code)}
                                                          disabled={groupDisabled}
                                                          onChange={() => handleFinAtticChange(code)}
                                                          style={{
                                                            marginRight: "0.35rem",
                                                          }}
                                                        />
                                                        {label}
                                                      </label>
                                                    ))}
                                                  </div>
                                                )}

                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </section>

              {/* Geographic overlays */}
              {selectedState &&
                (overlayMeta?.available?.hardware_stores ||
                  overlayMeta?.available?.food_stores) && (
                  <section>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.3rem",
                        fontSize: "0.8rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#9ca3af",
                      }}
                    >
                      Geographic Overlays
                    </label>

                    <div 
                      className="twyn-panel-scroll" 
                      style={scrollBoxStyle}>
                      {overlayDefinitions.map((def, idx) => {
                        // Only render if backend says this layer is available for this state/client
                        if (!overlayMeta?.available?.[def.key]) return null;

                        const currentSelections =
                          overlaySelections[def.key] || [];

                        return (
                          <div
                            key={def.key}
                            style={{ marginTop: idx > 0 ? "0.9rem" : 0 }}
                          >
                            {/* Header row */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "0.35rem",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "0.9rem",
                                  fontWeight: 600,
                                }}
                              >
                                {def.label}
                              </div>

                              {currentSelections.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => handleOverlayClear(def.key)}
                                  style={{
                                    border: "none",
                                    background: "none",
                                    padding: 0,
                                    margin: 0,
                                    fontSize: "0.75rem",
                                    cursor: "pointer",
                                    color: "#9ca3af",
                                  }}
                                  title="Clear selections"
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            {/* Dropdown */}
                            <select
                              value=""
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v) {
                                  handleOverlaySelect(def.key, v);
                                  e.target.value = "";
                                }
                              }}
                              style={{
                                width: "100%",
                                marginBottom: "0.45rem",
                                padding: "0.35rem",
                                background: "#020617",
                                color: "#e5e7eb",
                                border: "1px solid #4b5563",
                                borderRadius: "6px",
                              }}
                            >
                              <option value="" disabled>
                                {currentSelections.length === 0
                                  ? "All brands"
                                  : "Add a brand…"}
                              </option>
                              {(def.brands || []).map((b) => (
                                <option key={b} value={b}>
                                  {b}
                                </option>
                              ))}
                            </select>

                            {/* Chips */}
                            {currentSelections.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "0.35rem",
                                }}
                              >
                                {currentSelections.map((b) => (
                                  <span
                                    key={b}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.35rem",
                                      padding: "0.22rem 0.5rem",
                                      borderRadius: "999px",
                                      border: "1px solid #4b5563",
                                      background: "rgba(15,23,42,0.9)",
                                      color: "#e5e7eb",
                                      fontSize: "0.78rem",
                                    }}
                                  >
                                    {b === "ALL" ? "All brands" : b}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleOverlayRemove(def.key, b)
                                      }
                                      style={{
                                        border: "none",
                                        background: "none",
                                        padding: 0,
                                        margin: 0,
                                        cursor: "pointer",
                                        color: "#9ca3af",
                                        fontSize: "0.8rem",
                                        lineHeight: 1,
                                      }}
                                    >
                                      ✕
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}
            </div>

            {/* --- FIXED FOOTER SECTION (Compute Button) --- */}
            <div style={{ flexShrink: 0, paddingTop: "1rem" }}>
              <section>
                <button
                  type="button"
                  onClick={handleComputeScores}
                  disabled={!selectedState || isComputing}
                  className={[
                    "twyn-cta-button",
                    !selectedState && !isComputing
                      ? "twyn-cta-button--disabled-base"
                      : "",
                    isComputing ? "twyn-cta-button--loading" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    width: "100%",
                    padding: "0.6rem 0.5rem",
                    fontSize: "0.95rem",
                    borderRadius: "999px",
                    border: "none",
                    fontWeight: 600,
                    color: "white",
                    cursor:
                      !selectedState || isComputing
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {isComputing
                    ? "Finding Your Hidden Twins..."
                    : "Find My Hidden Twins"}
                </button>
                {computeError && (
                  <p
                    style={{
                      color: "#f97373",
                      fontSize: "0.8rem",
                      marginTop: "0.4rem",
                    }}
                  >
                    {computeError}
                  </p>
                )}
              </section>
            </div>
          </>
        )}
      </div>

      {/* TOP-RIGHT TWYN COUNT PANEL */}
      {selectedState && (
        <div
          ref={twynPanelRef} 
          style={{
            position: "absolute",
            top: 0,
            right: "4.5rem",
            marginTop: "1rem",
            zIndex: 10,
          }}
        >
          <div
            style={{
              background: "rgba(2, 6, 23, 0.94)",
              borderRadius: "8px",
              border: "1px solid #1f2937",
              boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
              padding: "0.75rem 1rem",
              width: "260px",
            }}
          >


            <div
              style={{
                fontSize: "1.6rem",
                fontWeight: 700,
                color: "#22D4F0",
                lineHeight: 1.1,
              }}
            >
              {twynCount.toLocaleString()} Twins
            </div>

            {/* Scenario description added here */}
            <div
              style={{
                marginTop: "0.3rem",
                fontSize: "0.75rem",
                lineHeight: "1.25rem",
                color: "#9ca3af",
                maxWidth: "240px",
              }}
            >
              Scenario {selectedScenarioIndex + 1}:{" "}
              {describeScenario(activeScenario)}
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM-RIGHT LEGEND */}
      {scoreResponse && (
        <div
          style={{
            position: "absolute",
            right: "4.5rem",
            bottom: "20px",
            zIndex: 30,
            background: "rgba(2,6,23,0.9)",
            border: "1px solid #1f2937",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            width: "260px",
            color: "#e5e7eb",
            boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
            fontSize: "0.8rem",
          }}
        >
          {/* Height only relevant in 3D */}
          {is3DView && (
            <div style={{ marginBottom: "0.75rem", color: "#9ca3af" }}>
              <strong>Height:</strong> N target individuals
            </div>
          )}

          <div style={{ marginBottom: "0.5rem", color: "#9ca3af" }}>
            <strong>Color:</strong>{" "}
            {is3DView ? "% target population" : "Priority score"}
          </div>

          {/* Gradient scale */}
          <div
            style={{
              height: "12px",
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, #020024 0%, #090979 25%, #064EA6 50%, #00D4FF 75%, #09feff 100%)",
              marginBottom: "0.5rem",
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "0.7rem",
              color: "#6b7280",
            }}
          >
            <span>{is3DView ? "Low %" : "Low priority"}</span>
            <span>{is3DView ? "High %" : "High priority"}</span>
          </div>
          
          {/* Opacity slider */}
          <div
            style={{
              marginTop: "0.6rem",
              borderTop: "1px solid #1f2937",
              paddingTop: "0.45rem",
            }}
          >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              fontSize: "0.8rem",
              color: "#9ca3af",
              marginBottom: "0.25rem",
              gap: "0.25rem",
            }}
          >
            <span>
              <strong>Opacity:</strong> {opacity.toFixed(2)}
            </span>
          </div>
            <input
              type="range"
              min={0.10}
              max={0.90}
              step={0.01}
              value={opacity}
              onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------
// App routing: Protected by Auth0 & authorization mapping
// ---------------------------------------------

function HomeRedirect({ user }) {
  if (hasPageAccess(user, "Demo")) {
    return <Navigate to="/Demo" replace />;
  }
  const allowed = getAllowedClients(user, ALL_CLIENTS);
  if (allowed.length > 0) {
    return <Navigate to={`/${allowed[0]}`} replace />;
  }
  return <Navigate to="/Demo" replace />;
}

function ClientRoute() {
  const { clientId } = useParams();
  const bypassTerms = clientId?.toLowerCase() === "demo";

  return (
    <TermsGate bypass={bypassTerms}>
      <ClientPage />
    </TermsGate>
  );
}

function ClientRouteWrapper() {
  const { clientId } = useParams();
  const { user, logout } = useAuth0();

  const hasAccess = hasPageAccess(user, clientId);

  if (!hasAccess) {
    const allowed = getAllowedClients(user, ALL_CLIENTS);
    return (
      <AccessDeniedScreen
        user={user}
        requestedClient={clientId}
        allowedClients={allowed}
        logout={logout}
      />
    );
  }

  return <ClientRoute />;
}

function App() {
  const { isAuthenticated, isLoading, user, error, loginWithRedirect } = useAuth0();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          height: "100vh",
          background: "#122035",
          color: "#ffffff",
          fontFamily: "Avenir, sans-serif",
          textAlign: "center",
          padding: "2rem",
        }}
      >
        <h2 style={{ color: "#f97373", marginBottom: "1rem" }}>Configuration Error</h2>
        <p style={{ maxWidth: "500px", lineHeight: "1.6", color: "#B2CCEE", marginBottom: "2rem" }}>
          {error.message || "Failed to authenticate. Please verify that the Allowed Callback URLs and Web Origins are correctly configured in the Auth0 Application Settings."}
        </p>
        <button
          onClick={() => window.location.href = "/"}
          style={{
            padding: "0.8rem 1.5rem",
            background: "linear-gradient(135deg, #0A74D6 0%, #004174 100%)",
            border: "none",
            borderRadius: "10px",
            color: "white",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginGateway loginWithRedirect={loginWithRedirect} />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomeRedirect user={user} />} />
      <Route path="/:clientId" element={<ClientRouteWrapper />} />
    </Routes>
  );
}

export default App;
